/**
 * kick-kw (2026-10-23) — 콘텐츠 생성기가 쓰는 「킥 키워드」 선택기.
 *
 * 재료 — site_kick_keywords(크론 app/api/cron/kick-keywords 가 /keywordstool 로 채운다).
 * 쓰는 법(생성기 쪽):
 *   const kick = await loadKickKeywords(sb, siteIds);                     // 한 번에 한 쿼리
 *   const pick = pickKickKeywords(kick.get(site.id) ?? [], site, {
 *     stage: kickStageOf(site.lifecycle_stage), max: KICK_USAGE_LIMITS.total,
 *     facts: { price: site.price_min != null, modelHouse: !!site.model_house_open_date || hasNotice },
 *   });
 *   pick.phrases → 제목 1 · h2 ≤2 · FAQ 질문 ≤2 · 본문 문장 ≤2 (KICK_USAGE_LIMITS) — 넘기면 키워드 반복(스터핑)이다.
 *
 * ⛔ 사실로 받칠 수 없는 수식어는 고르지 않는다 — 분양가를 모르면 「분양가」, 모델하우스(오픈일·공고)가 없으면
 *    「모델하우스」를 제목·h2 에 걸지 않는다(검색어만 걸고 내용이 비면 이탈 + 오정보 관문 위반).
 *    판정 재료는 호출자가 facts 로 넘긴다(이 모듈은 DB 의 현장 행을 다시 읽지 않는다).
 * ⛔ 측정이 없으면(행 0) 사전값(priors — keyword_rank_targets 수식어별 평균 검색량 실측)으로만 순서를 정한다.
 *    그 경우 source='prior' — 「검색량 n」 같은 수치를 본문에 쓰지 말 것.
 */
import { kickNameOf, kickNorm, buildSiteProfile, type KickSiteInput } from '@/lib/seo/kick-keywords-collect';
import { LEXICON_TERMS, lexNeedOf, lexiconStageList } from '@/lib/seo/kw-lexicon';

/* ───────────────────────────── 상수 ───────────────────────────── */

/** 한 글에 킥 문구를 넣는 상한(스터핑 방지). total 은 phrases 개수 상한의 기본값. */
export const KICK_USAGE_LIMITS = { title: 1, h2: 2, faq: 2, body: 2, total: 6 } as const;

/** 최소 월간 검색량(PC+모바일). 「< 10」 행은 항상 뺀다. */
export const KICK_MIN_VOLUME = 30;

/**
 * 수식어 사전값 — keyword_rank_targets 실측 검색량의 수식어별 평균(2026-10-23 집계).
 * 행이 없는 현장의 순서 정하기에만 쓴다.
 */
export const KICK_MODIFIER_PRIORS: Readonly<Record<string, number>> = {
  청약: 2253, 시세: 1793, 청약일정: 1407, 재건축: 495, 미분양: 335, 재개발: 330, 모델하우스: 258, 분양권: 232, 입주: 142, 분양가: 126,
};

export type KickStage = 'subscription' | 'upcoming' | 'redev' | 'unsold';

/** 단계별로 «맞는» 수식어(앞이 우선 — 같은 검색량이면 이 순서). 목록에 없는 수식어는 그 단계에서 안 쓴다. */
const KICK_STAGE_MODIFIERS_BASE: Readonly<Record<KickStage, readonly string[]>> = {
  subscription: ['청약일정', '청약', '분양가', '모델하우스', '경쟁률', '당첨', '무순위', '평면도', '입주'],
  upcoming: ['분양일정', '분양가', '모델하우스', '위치', '청약', '청약일정', '평면도', '시세'],
  redev: ['재개발', '재건축', '일반분양', '시세', '분양권', '분양일정', '위치'],
  unsold: ['미분양', '무순위', '줍줍', '분양가', '모델하우스', '시세', '입주'],
};
/**
 * kw-mine (2026-10-25) — 기존 단계 목록 «뒤에» 공통 수식어 사전(lib/seo/kw-lexicon)의 단계 항목을 덧붙인다.
 *   기존 순서·우선은 그대로(같은 검색량이면 앞이 이긴다). 새 항목은 대부분 사실 관문(MODIFIER_NEEDS — 기본 false)이 있어
 *   호출자가 그 사실 깃발을 넘기기 전엔 고르지 않는다(노출 0 · blocked 로만 보고).
 */
const withLexicon = (st: KickStage): readonly string[] => [...new Set([...KICK_STAGE_MODIFIERS_BASE[st], ...lexiconStageList(st)])];
export const KICK_STAGE_MODIFIERS: Readonly<Record<KickStage, readonly string[]>> = {
  subscription: withLexicon('subscription'),
  upcoming: withLexicon('upcoming'),
  redev: withLexicon('redev'),
  unsold: withLexicon('unsold'),
};

/**
 * KWX 레인 D(실측 F11) — 조합 탐침에 늘 넣는 상세 꼬리(평면도·조감도). /keywordstool 연관어는 「{이름}평면도」 를 거의 안 돌려줘
 *   측정 행이 0이었다 → 분양 전·청약 단계 탐침 5칸 중 2칸을 이 꼬리에 준다(나머지 3칸 = 채굴 순서 상위). 선택은 여전히 측정 검색량 순.
 */
export const KICK_TAIL_PROBES: Readonly<Partial<Record<KickStage, readonly string[]>>> = {
  subscription: ['평면도', '조감도'],
  upcoming: ['평면도', '조감도'],
};
export function probeModifiersFor(stage: KickStage, ordered: readonly string[], max = 5): string[] {
  const tails = [...(KICK_TAIL_PROBES[stage] ?? [])].slice(0, Math.max(0, max));
  const head = ordered.filter((m) => !tails.includes(m)).slice(0, Math.max(0, max - tails.length));
  return [...head, ...tails];
}

/** lifecycle_stage → 선택 단계. */
export function kickStageOf(lifecycleStage: string | null | undefined): KickStage {
  switch (String(lifecycleStage ?? '')) {
    case 'subscription_open': case 'award_pending': case 'award_announced': return 'subscription';
    case 'unsold_active': return 'unsold';
    case 'union_established': case 'constructor_selected': case 'plan_approved': case 'mgmt_approved': case 'construction': return 'redev';
    default: return 'upcoming';
  }
}

/** 호출자가 넘기는 «받칠 수 있는 사실» 깃발. 기본값: 일정은 있다고 보고, 분양가·모델하우스·경쟁률은 없다고 본다. */
export interface KickFacts {
  /** 분양가(price_min/max 또는 공고 분양가) 확보 */
  price?: boolean;
  /** 모델하우스 오픈일 또는 모집공고 확보 */
  modelHouse?: boolean;
  /** 청약·분양 일정(공고 날짜 또는 예상월) 확보 — 기본 true */
  schedule?: boolean;
  /** 경쟁률(접수 결과) 확보 */
  competition?: boolean;
  /* ── kw-mine 확장 사실(기본 false — 넘기기 전엔 해당 수식어 노출 0) ── */
  /** 입주자모집공고 본문(전매·실거주·계약금·중도금·옵션·조감도·커뮤니티 근거) 확보 */
  notice?: boolean;
  /** 주택형(타입·평형) 표 확보 */
  types?: boolean;
  /** 위치 근거(주소·좌표 + 학교·역 거리) 확보 */
  location?: boolean;
  /** 정비 단계 근거(관리처분·사업시행인가·시공사·분담금 원장) 확보 */
  redevStage?: boolean;
  /** 잔여세대·선착순·할인 조건 근거 확보 */
  unsoldDetail?: boolean;
  /** 입주 예정일·사전점검 일정 확보 */
  moveIn?: boolean;
  /** 실거래·분양권 거래 근거 확보 */
  trade?: boolean;
}

/** 수식어 → 필요한 사실. 여기 없는 수식어는 제한 없음. */
const MODIFIER_NEEDS: Readonly<Record<string, keyof KickFacts>> = {
  // 사전의 관문(새 수식어) → 기존 명시 관문이 덮는다(기존 동작 불변).
  ...Object.fromEntries(LEXICON_TERMS.map((t) => [t, lexNeedOf(t)]).filter(([, n]) => !!n)),
  분양가: 'price', 모델하우스: 'modelHouse', 견본주택: 'modelHouse', 청약일정: 'schedule', 분양일정: 'schedule',
  경쟁률: 'competition', 당첨: 'competition',
};

/** 같은 뜻 묶음(한 글에 하나만). */
const MODIFIER_GROUP: Readonly<Record<string, string>> = { 견본주택: '모델하우스', 줍줍: '무순위' };

/** 수식어가 지금 쓸 수 있는가(사실 깃발 기준). */
export function modifierSupported(modifier: string, facts: KickFacts = {}): boolean {
  const need = MODIFIER_NEEDS[modifier];
  if (!need) return true;
  const f = { schedule: true, ...facts };
  return !!f[need];
}

/* ───────────────────────────── 적재 행 읽기 ───────────────────────────── */

export interface KickRow {
  site_id: string;
  keyword: string;
  modifier: string | null;
  volume_total: number | null;
  is_lt10: boolean | null;
  comp_idx?: string | null;
  checked_at?: string | null;
}

/**
 * 현장들의 킥 키워드(한 쿼리). 실패하면 빈 Map — 생성기는 사전값으로 내려간다(던지지 않는다).
 * 검색량 내림으로 온다. 「< 10」 행은 DB 에서부터 뺀다.
 */
export async function loadKickKeywords(sb: any, siteIds: readonly string[]): Promise<Map<string, KickRow[]>> {
  const ids = [...new Set(siteIds.filter(Boolean))];
  const out = new Map<string, KickRow[]>();
  if (!ids.length) return out;
  try {
    const { data, error } = await sb
      .from('site_kick_keywords')
      .select('site_id, keyword, modifier, volume_total, is_lt10, comp_idx, checked_at')
      .in('site_id', ids)
      .eq('is_lt10', false)
      .order('volume_total', { ascending: false, nullsFirst: false })
      .limit(Math.min(ids.length * 50, 10000));
    if (error || !Array.isArray(data)) return out;
    for (const r of data as KickRow[]) {
      const l = out.get(r.site_id);
      if (l) l.push(r); else out.set(r.site_id, [r]);
    }
  } catch { /* 모름 → 빈 Map */ }
  return out;
}

/**
 * 지역어 대체 재료 — keyword_rank_targets 에서 후보 지역어(공백 유·무)의 검색량(한 쿼리).
 * site_kick_keywords 에 area 행이 없는 현장의 생활권 키워드용. 실패 = 빈 배열.
 */
export async function loadAreaFallback(sb: any, candidates: readonly string[]): Promise<Array<{ keyword: string; volume_total: number | null }>> {
  const kws = [...new Set(candidates.filter(Boolean))];
  if (!kws.length) return [];
  try {
    const { data, error } = await sb
      .from('keyword_rank_targets')
      .select('keyword, volume_total')
      .in('keyword', kws)
      .not('volume_total', 'is', null);
    if (error || !Array.isArray(data)) return [];
    return data as Array<{ keyword: string; volume_total: number | null }>;
  } catch { return []; }
}

/* ───────────────────────────── 선택 ───────────────────────────── */

export interface KickPick {
  /** 글에 쓸 문구(공백 넣은 형태) */
  phrase: string;
  /** 원 키워드(공백 없는 측정 형태) — 사전값이면 phrase 의 공백 제거 */
  keyword: string;
  modifier: string | null;
  /** 월간 검색량(측정) — 사전값이면 null */
  volume: number | null;
  source: 'measured' | 'prior';
}

export interface KickPickResult {
  phrases: KickPick[];
  /** 생활권·지역 대표 키워드(없으면 null) */
  area: KickPick | null;
  /** 측정 행이 있었나 */
  source: 'measured' | 'prior' | 'none';
  /** 사실이 없어 뺀 수식어(호출자가 사실을 채우면 쓸 수 있다) */
  blocked: string[];
}

export interface KickPickOptions {
  stage?: KickStage;
  max?: number;
  minVolume?: number;
  facts?: KickFacts;
  /** loadAreaFallback 결과(선택) */
  areaFallback?: ReadonlyArray<{ keyword: string; volume_total: number | null }>;
}

/** 측정 키워드에 공백을 넣는다 — 머리가 현장 이름(공백 제거)과 같으면 이름 원형 + ' ' + 수식어. */
export function spacedPhrase(keyword: string, modifier: string | null, names: readonly string[]): string {
  const k = String(keyword ?? '').trim();
  if (!modifier || !k.endsWith(modifier) || k.length === modifier.length) return k;
  const head = k.slice(0, k.length - modifier.length).trim();
  const hn = kickNorm(head);
  const named = names.find((n) => kickNorm(n) === hn);
  return `${named ?? head} ${modifier}`;
}

/**
 * 순수 — 한 현장의 킥 문구를 고른다.
 *   ① 단계에 맞는 수식어만(KICK_STAGE_MODIFIERS) ② 사실로 받칠 수 있는 것만(facts) ③ 검색량 ≥ minVolume · 「< 10」 제외
 *   ④ 공백 차이 중복 제거 · 같은 뜻 수식어(견본주택=모델하우스) 하나만 ⑤ 검색량 내림(같으면 단계 목록 순).
 *   측정 행이 하나도 남지 않으면 사전값(KICK_MODIFIER_PRIORS 내림 → 단계 목록 순)으로 「{이름} {수식어}」 를 만든다.
 *   정비 단계 재개발/재건축은 현장 이름에 든 쪽만(사전값 경로).
 */
export function pickKickKeywords(rows: readonly KickRow[], site: KickSiteInput, opts: KickPickOptions = {}): KickPickResult {
  const stage = opts.stage ?? kickStageOf(site.lifecycle_stage);
  const max = Math.max(0, opts.max ?? KICK_USAGE_LIMITS.total);
  const minVol = opts.minVolume ?? KICK_MIN_VOLUME;
  const facts = opts.facts ?? {};
  const allowed = KICK_STAGE_MODIFIERS[stage];
  const rank = (m: string) => { const i = allowed.indexOf(MODIFIER_GROUP[m] ?? m); return i < 0 ? allowed.indexOf(m) : i; };
  const prof = buildSiteProfile(site);
  const displayName = kickNameOf(site) ?? String(site.name ?? '').trim();
  const names = [displayName, prof?.core ?? ''].filter(Boolean);
  const blocked = new Set<string>();

  const ok = (r: KickRow) => !r.is_lt10 && r.volume_total != null && r.volume_total >= minVol;

  // ── 측정 경로
  const measured = rows.filter((r) => r.modifier && r.modifier !== 'area' && ok(r));
  const cands = measured
    .filter((r) => allowed.includes(r.modifier!) || allowed.includes(MODIFIER_GROUP[r.modifier!] ?? ''))
    .filter((r) => { if (modifierSupported(r.modifier!, facts)) return true; blocked.add(r.modifier!); return false; })
    .sort((a, b) => (b.volume_total ?? 0) - (a.volume_total ?? 0) || rank(a.modifier!) - rank(b.modifier!));
  const seenKw = new Set<string>();
  const seenGroup = new Set<string>();
  const phrases: KickPick[] = [];
  for (const r of cands) {
    if (phrases.length >= max) break;
    const kn = kickNorm(r.keyword);
    const g = MODIFIER_GROUP[r.modifier!] ?? r.modifier!;
    if (seenKw.has(kn) || seenGroup.has(g)) continue;
    seenKw.add(kn); seenGroup.add(g);
    phrases.push({ phrase: spacedPhrase(r.keyword, r.modifier, names), keyword: r.keyword, modifier: r.modifier, volume: r.volume_total, source: 'measured' });
  }

  let source: KickPickResult['source'] = phrases.length ? 'measured' : 'none';
  // ── 사전값 경로(측정 0)
  if (!phrases.length && displayName && max > 0) {
    const nameN = kickNorm(displayName);
    const redevKind = nameN.includes('재건축') ? '재건축' : '재개발';
    const pri = [...allowed]
      .filter((m) => !(stage === 'redev' && (m === '재개발' || m === '재건축') && m !== redevKind))
      .filter((m) => { if (modifierSupported(m, facts)) return true; blocked.add(m); return false; })
      .sort((a, b) => (KICK_MODIFIER_PRIORS[b] ?? 0) - (KICK_MODIFIER_PRIORS[a] ?? 0) || rank(a) - rank(b));
    for (const m of pri) {
      if (phrases.length >= max) break;
      // 이름이 이미 그 수식어로 끝나면(「문현3 재개발」 + 재개발) 덧붙이지 않는다.
      if (nameN.endsWith(m)) continue;
      const phrase = `${displayName} ${m}`;
      phrases.push({ phrase, keyword: kickNorm(phrase), modifier: m, volume: null, source: 'prior' });
    }
    if (phrases.length) source = 'prior';
  }

  // ── 지역어
  let area: KickPick | null = null;
  const areaRow = rows.filter((r) => r.modifier === 'area' && ok(r)).sort((a, b) => (b.volume_total ?? 0) - (a.volume_total ?? 0))[0];
  if (areaRow) area = { phrase: areaRow.keyword, keyword: areaRow.keyword, modifier: 'area', volume: areaRow.volume_total, source: 'measured' };
  else if (opts.areaFallback?.length) {
    const best = [...opts.areaFallback]
      .filter((r) => r.volume_total != null && r.volume_total >= minVol)
      .sort((a, b) => (b.volume_total ?? 0) - (a.volume_total ?? 0))[0];
    if (best) area = { phrase: best.keyword, keyword: kickNorm(best.keyword), modifier: 'area', volume: best.volume_total, source: 'measured' };
  }

  // 측정 경로에서 막힌 것 중 실제로 고른 묶음과 겹치는 건 보고에서 뺀다.
  for (const p of phrases) if (p.modifier) blocked.delete(p.modifier);
  return { phrases, area, source, blocked: [...blocked] };
}
